import { Box, Fab, Pagination } from "@mui/material";
import { WarnIcon } from "../assets/icons/icons";
import theme from "../theme";
import { useEffect, useState, useRef } from "react";
import { ModalLayout } from "./common/ModalLayout";
import { useWindowSize } from "@reactuses/core";
import useGetMobilityCheck from "../hooks/useGetMobilityCheck";
import styled from "styled-components";
import Spinner from "./spinner";
import { deviceImageArray } from "../utils/deviceImageArray";
import { formatDate } from "../utils/util";
import { useLocation } from "react-router-dom";
import { useI18n } from "../locales";
import { getMobilityTypeText } from "../utils/langUtils";

const { md } = theme.screen;

const getToday = () => {
  // YYYY-MM-DD 형식의 오늘 날짜 문자열 반환
  const today = new Date();
  return today.toISOString().split("T")[0];
};

/**
 * 금일 점검 요망 농기계 목록 확인용 버튼과 모달
 */
export const DailyWarning = () => {
  const [openModal, setOpenModal] = useState(false);
  const { width } = useWindowSize();
  const location = useLocation();
  const [page, setPage] = useState(0);
  const today = getToday(); // 오늘 날짜
  const [isCheck, setIsCheck] = useState(false); // 오늘 확인했는지 여부
  const [isNoData, setIsNoData] = useState(false); // 확인할 데이터가 없음
  const [isFolded, setIsFolded] = useState(false); // 폴딩 상태
  const foldTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isControlPage = location.pathname === "/"; // 사고관제 화면인지 확인
  const { t } = useI18n();

  const { data, isLoading } = useGetMobilityCheck({
    limit: 10,
    page: page,
  });

  useEffect(() => {
    const savedDate = localStorage.getItem("DAILY_CHECK");

    // 로컬스토리지에 저장된 날짜가 오늘이면 확인완료 / 아니면 확인필요
    if (savedDate === today) {
      setIsCheck(true);
    } else {
      setIsCheck(false);
    }

    // 데이터가 없거나 리스트가 비어 있으면 true -> 경고 표출 안함
    if (!data || !data.list || data.list.length === 0) {
      setIsNoData(true);
    } else {
      setIsNoData(false);
    }
  }, [data]);

  // 사고관제 화면에서만 자동 폴딩 기능 적용
  useEffect(() => {
    if (!isControlPage || width <= theme.screen.md || openModal) {
      setIsFolded(false);
      // 사고관제 화면이 아니거나 모달이 열려있으면 타이머 정리
      if (foldTimeoutRef.current) {
        clearTimeout(foldTimeoutRef.current);
        foldTimeoutRef.current = null;
      }
      return;
    }

    // 페이지 진입 시 초기 상태는 보이는 상태
    setIsFolded(false);
    
    // 10초 후에 폴딩
    foldTimeoutRef.current = setTimeout(() => {
      setIsFolded(true);
    }, 10000);

    return () => {
      if (foldTimeoutRef.current) {
        clearTimeout(foldTimeoutRef.current);
      }
    };
  }, [isControlPage, width, location.pathname, openModal]);

  // 마우스가 버튼 근처를 지나거나 버튼 위에 있을 때
  const handleMouseEnter = () => {
    if (!isControlPage || width <= theme.screen.md) return;
    
    // 타이머 취소
    if (foldTimeoutRef.current) {
      clearTimeout(foldTimeoutRef.current);
      foldTimeoutRef.current = null;
    }
    
    // 폴딩 해제
    setIsFolded(false);
  };

  // 마우스가 버튼 영역을 벗어날 때
  const handleMouseLeave = () => {
    if (!isControlPage || width <= theme.screen.md || openModal) return;
    
    // 모달이 닫혀있을 때만 10초 후에 다시 폴딩
    foldTimeoutRef.current = setTimeout(() => {
      setIsFolded(true);
    }, 10000);
  };

  const handlePageChange = (
    // 페이지네이션
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value - 1);
  };

  // 데이터가 없으면 아무것도 표출하지 않음
  if (isNoData && !isLoading) return null;

  // 사고관제 화면에서 폴딩된 상태일 때 right 값 계산
  const getRightValue = () => {
    if (!isControlPage || width <= theme.screen.md) {
      return width <= theme.screen.md ? "none" : -10;
    }
    // 폴딩된 상태: 버튼의 일부가 보이도록 살짝만 이동 (사용자가 버튼이 있다는 것을 알 수 있게)
    // 안 폴딩된 상태: 원래 위치
    // 버튼 너비가 60px이므로 약 -50 정도로 설정하여 버튼의 일부가 보이도록 함
    return isFolded ? (width <= theme.screen.md ? "none" : -50) : -10;
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        zIndex: 71, // 헤더보다 위
        position: "fixed",
        top: width <= theme.screen.md ? -20 : 10,
        right: getRightValue(),
        left: width <= theme.screen.md ? 100 : "none",
        transition: "right 0.3s ease-in-out",
        "@keyframes blinkBg": {
          "0%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.5,
          },
          "100%": {
            opacity: 1,
          },
        },
        "& .MuiFab-root": {
          animation: isCheck ? "none" : "blinkBg 0.7s infinite",
          backgroundColor: theme.color.pastelOrange_500,
          borderRadius: "6px",
          height: width <= theme.screen.md ? "60px" : "80px",
          width: width <= theme.screen.md ? "80px" : "60px",
          padding:
            width <= theme.screen.md
              ? "26px 0px 10px 0px"
              : "0 16px 0 10px",
          ":hover": {
            backgroundColor: theme.color.orange_400,
          },
          svg: {
            paddingBottom: "2px", // 중앙이 안 맞아서 추가함
            flexShrink: 0,
          },
        },
      }}
    >
      <Fab
        variant="extended"
        size="small"
        onClick={() => {
          setOpenModal(true);
          setIsCheck(true);
          localStorage.setItem("DAILY_CHECK", today);
        }}
      >
        <WarnIcon />
      </Fab>

      {openModal && (
        <ModalLayout
          open={openModal}
          onClose={() => setOpenModal(false)}
          title={`${t.control.dailyInspectionRequiredMachineryList}${
            data?.total_items ? ` - (${data.total_items} ${t.statistics.units})` : ""
          }`}
          width={width <= theme.screen.md ? "100%" : "600px"}
        >
          <Box
            sx={{
              width:
                width <= theme.screen.md ? "calc(100vw - 104px)" : "560px",
              // height: "500px",
            }}
          >
            <List>
              {isLoading && <Spinner />}
              {data?.list &&
                data?.list.map((el) => {
                  return (
                    <ListItem
                      key={el.seq.toString()}
                      role="button"
                      tabIndex={0}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <ImageWrapper
                          bgColor={theme.color.neutral_100}
                          bdColor={"rgba(26, 39, 35, 0.5)"}
                        >
                          {el?.mobility_image === null ? (
                            <CustomImg
                              src={`${
                                process.env.REACT_APP_BASE_URL
                              }/img/${
                                deviceImageArray.find(
                                  (list) =>
                                    list.device === el.mobility_type
                                )?.listFileName
                              }`}
                              alt="농기계 이미지"
                            />
                          ) : (
                            <CustomImg
                              src={`${process.env.REACT_APP_BASE_URL}/${el?.mobility_image}`}
                              alt="농기계 이미지"
                              isEtc
                            />
                          )}
                        </ImageWrapper>
                      </Box>
                      <InfoWrapper>
                        <First>{el.mobility_name}</First>
                        <First>
                          {`[${getMobilityTypeText(el.mobility_type, t)}] - ${el.device_pid}`}
                        </First>

                        <Second>{t.control.owner} : {el.name}</Second>

                        <Third>
                          {t.statistics.lastOperation} :
                          {!el.max_registered_at && el.min_registered_at
                            ? formatDate(
                                el.min_registered_at,
                                "yyyy-MM-dd HH:mm"
                              )
                            : el.max_registered_at && !el.min_registered_at
                            ? formatDate(
                                el.max_registered_at,
                                "yyyy-MM-dd HH:mm"
                              )
                            : ` ${t.statistics.noDrivingRecord}`}
                        </Third>
                      </InfoWrapper>
                    </ListItem>
                  );
                })}

              {data && data.list.length > 0 && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px 0",
                  }}
                >
                  <Pagination
                    page={page + 1}
                    count={data?.total_page}
                    onChange={handlePageChange}
                    // siblingCount={width > md ? 4 : width > sm ? 3 : 2}
                    size="small"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      justifySelf: "end",
                    }}
                  />
                </Box>
              )}
              {!isLoading && (!data || data?.list.length === 0) && (
                <NoDataMassage>{t.control.noData}</NoDataMassage>
              )}
            </List>
          </Box>
        </ModalLayout>
      )}
    </Box>
  );
};

const List = styled.div`
  flex: 1;
`;

export const ListItem = styled.div<{
  active?: boolean;
  accentColor?: string;
  hoverColor?: string;
}>`
  display: flex;
  gap: 20px;
  padding: 22px 20px;
  border-bottom: 1px solid ${theme.color.neutral_200};
  background-color: ${({ active, hoverColor }) =>
    active ? hoverColor : theme.color.white};

  &:hover {
    background-color: ${({ hoverColor }) => hoverColor};
  }
  border-left: ${({ active, accentColor }) =>
    active ? `5px solid ${accentColor}` : "none"};
  order: ${({ active }) => active && "-1"};
  position: relative;

  @media (max-width: ${md}px) {
    gap: 15px;
    padding: 22px 0px;
  }
`;

export const ImageWrapper = styled.div<{
  bgColor: string;
  bdColor: string;
}>`
  background-color: ${({ bgColor }) => bgColor};
  width: 65px;
  height: 61px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: ${({ bdColor }) => `3px solid ${bdColor}`};
  flex-shrink: 0;
`;

const CustomImg = styled.img<{ isEtc?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: ${({ isEtc }) => (isEtc ? `fill` : `contain`)};
`;
export const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const First = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${theme.color.neutral_700};
  @media (max-width: ${md}px) {
    font-size: 15px;
  }
`;

export const Second = styled.span`
  font-weight: 700;
  color: ${theme.color.neutral_700};
  font-size: 14px;
  line-height: 16px;
  @media (max-width: ${md}px) {
    font-size: 13px;
  }
`;

export const Third = styled.span`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${theme.color.neutral_500};

  @media (max-width: ${md}px) {
    font-size: 10px;
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  padding: 50px;
  color: ${theme.color.neutral_500};
`;
